// Reusable "Download PDF" button. Wraps PDFDownloadLink from @react-pdf/renderer
// and styles it to match the DIDI palette. Renders inline (caller positions it).
import React from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { AnalysisReportPDF } from './AnalysisReportPDF';
import type { AnalysisSession } from '../../types/analysis-session';
import type { TechniqueDefinition } from '../../services/technique-definitions.service';

interface Props {
  session: AnalysisSession | null | undefined;
  /** Optional technique definitions for inline descriptions. */
  techDefs?: TechniqueDefinition[];
  /** Compact mode for use in lists (smaller, icon-only on narrow). */
  compact?: boolean;
}

const Btn = styled.button<{ compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%);
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  padding: ${p => p.compact ? '6px 10px' : '9px 14px'};
  font-size: ${p => p.compact ? '11px' : '13px'};
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: filter 0.15s ease, transform 0.1s ease;
  text-decoration: none;

  &:hover { filter: brightness(1.1); }
  &:active { transform: translateY(1px); }
  &:disabled { opacity: 0.6; cursor: wait; filter: grayscale(0.3); }
`;

const Disabled = styled.span<{ compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(124, 58, 237, 0.15);
  color: #A78BFA;
  border: 1px solid rgba(124, 58, 237, 0.3);
  border-radius: 8px;
  padding: ${p => p.compact ? '6px 10px' : '9px 14px'};
  font-size: ${p => p.compact ? '11px' : '13px'};
  font-weight: 600;
  cursor: not-allowed;
  font-family: inherit;
`;

export const DownloadPdfButton: React.FC<Props> = ({ session, techDefs, compact = false }) => {
  const { t, i18n } = useTranslation();
  const lang: 'ro' | 'en' = (i18n.language || '').toLowerCase().startsWith('ro') ? 'ro' : 'en';
  const labelDownload = t('analysis.pdf.btnDownload');
  const labelGenerating = t('analysis.pdf.btnGenerating');

  if (!session || !session.session_id) {
    return (
      <Disabled compact={compact}>
        <Download size={compact ? 12 : 14} />
        {labelDownload}
      </Disabled>
    );
  }

  const dateStr = new Date().toISOString().split('T')[0];
  const filename = `didi-report-${session.session_id.substring(0, 8)}-${dateStr}.pdf`;

  return (
    <PDFDownloadLink
      document={<AnalysisReportPDF session={session} language={lang} techDefs={techDefs} />}
      fileName={filename}
      style={{ textDecoration: 'none' }}
    >
      {({ loading, error }) => (
        <Btn compact={compact} disabled={loading} type="button">
          <Download size={compact ? 12 : 14} />
          {loading ? labelGenerating : (error ? t('analysis.pdf.btnError') : labelDownload)}
        </Btn>
      )}
    </PDFDownloadLink>
  );
};

export default DownloadPdfButton;
